import { z } from 'zod';

import { organizationSchema } from '../organization';
import { CancelReason, organizationBillingSchema } from '../organization-billing';
import {
  IntegrationProvider,
  IntegrationStatus,
  OrganizationIntegrationError,
  externalUrlSchema,
  shopifyExternalUrlSchema,
} from '../organization-integration';
import {
  OrganizationInviteStatus,
  OrganizationUserRole,
  allUserRoles,
  organizationUserSchema,
} from '../organization-user';
import { mongoIdSchema } from '../shared';
import { userSchema } from '../user';

// List
export const listReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type ListReq = z.infer<typeof listReqSchema>;

export const listResSchema = {
  200: z.object({
    data: z.object({
      organizations: z.array(organizationSchema),
      organizationUsers: z.array(organizationUserSchema),
    }),
  }),
};
export type ListRes = z.infer<(typeof listResSchema)[200]>;

// GetById
export const getOrganizationByIdReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetOrganizationByIdReq = z.infer<typeof getOrganizationByIdReqSchema>;

export const getOrganizationByIdResSchema = {
  200: z.object({
    data: organizationSchema,
  }),
};
export type GetOrganizationByIdRes = z.infer<(typeof getOrganizationByIdResSchema)[200]>;

// Save
export const saveOrganizationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: organizationSchema.partial().strip(),
});
export type SaveOrganizationReq = z.infer<typeof saveOrganizationReqSchema>;

export const saveOrganizationResSchema = {
  200: z.object({
    data: organizationSchema,
  }),
};
export type SaveOrganizationRes = z.infer<(typeof saveOrganizationResSchema)[200]>;

// GetBillingByOrgId
export const getBillingByOrgIdReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetBillingByOrgIdReq = z.infer<typeof getBillingByOrgIdReqSchema>;

export const getBillingByOrgIdResSchema = {
  200: z.object({
    data: organizationBillingSchema,
  }),
};
export type GetBillingByOrgIdRes = z.infer<(typeof getBillingByOrgIdResSchema)[200]>;

// SyncBillingByOrgIdWithPaymentProvider
export const syncBillingByOrgIdWithPaymentProviderReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type SyncBillingByOrgIdWithPaymentProviderReq = z.infer<
  typeof syncBillingByOrgIdWithPaymentProviderReqSchema
>;

export const syncBillingByOrgIdWithPaymentProviderResSchema = {
  200: z.object({
    data: organizationBillingSchema,
  }),
};
export type SyncBillingByOrgIdWithPaymentProviderRes = z.infer<
  (typeof syncBillingByOrgIdWithPaymentProviderResSchema)[200]
>;

// CheckProviderSession
export const checkProviderSessionReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({ sessionId: z.string() }).strip(),
});
export type CheckProviderSessionReq = z.infer<typeof checkProviderSessionReqSchema>;

export const checkProviderSessionResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type CheckProviderSessionRes = z.infer<(typeof checkProviderSessionResSchema)[200]>;

// List users
export const listUsersReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type ListUsersReq = z.infer<typeof listUsersReqSchema>;

export const listUsersResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
    users: z.array(userSchema),
  }),
};
export type ListUsersRes = z.infer<(typeof listUsersResSchema)[200]>;

// Invite user
export const inviteUsersReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.array(
    z
      .object({
        userEmail: userSchema.shape.email,
        role: z.enum([OrganizationUserRole.Member]),
      })
      .strip()
  ),
});
export type InviteUsersReq = z.infer<typeof inviteUsersReqSchema>;

export const inviteUsersResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
    users: z.array(userSchema),
  }),
};
export type InviteUsersRes = z.infer<(typeof inviteUsersResSchema)[200]>;

// Transfer owernship
export const transferOwnershipReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({ toUserId: userSchema.shape._id }).strip(),
});
export type TransferOwernshipReq = z.infer<typeof transferOwnershipReqSchema>;

export const transferOwnershipResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
    users: z.array(userSchema),
  }),
};
export type TransferOwernshipRes = z.infer<(typeof transferOwnershipResSchema)[200]>;

// Manage user
export const manageUserReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      userId: userSchema.shape._id,
      role: allUserRoles,
      roleLabel: z.string(),
      delete: z.boolean().nullish(),
      deleteData: z
        .object({
          reason: z.nativeEnum(CancelReason),
          extraDetails: z.string(),
        })
        .nullish(),
    })
    .strip(),
});
export type ManageUserReq = z.infer<typeof manageUserReqSchema>;

export const manageUserResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
    users: z.array(userSchema),
  }),
};
export type ManageUserRes = z.infer<(typeof manageUserResSchema)[200]>;

// Manage invitaton on organization
export const manageInvitationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      status: z.enum([OrganizationInviteStatus.Rejected, OrganizationInviteStatus.Accepted]),
    })
    .strip(),
});
export type ManageInvitationReq = z.infer<typeof manageInvitationReqSchema>;

export const manageInvitationResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type ManageInvitationRes = z.infer<(typeof manageInvitationResSchema)[200]>;

//////////////////////////
// Organization get socket event

export const getOrganizationSocketResSchema = z
  .object({
    organization: organizationSchema,
  })
  .or(
    z.object({
      organizationId: organizationSchema.shape._id,
      hasError: z.boolean(),
      isDeleted: z.boolean(),
    })
  );
export type GetOrganizationSocketRes = z.infer<typeof getOrganizationSocketResSchema>;

// Revoke invite
export const revokeInviteReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({
    userEmail: userSchema.shape.email,
  }),
});
export type RevokeInviteReq = z.infer<typeof revokeInviteReqSchema>;

export const revokeInviteResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
  }),
};
export type RevokeInviteRes = z.infer<(typeof revokeInviteResSchema)[200]>;

export const getOrgUsersFromEmailReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    email: userSchema.shape.email,
  }),
});
export type GetOrgUsersFromEmailReq = z.infer<typeof getOrgUsersFromEmailReqSchema>;

export const getOrgUsersFromEmailResSchema = {
  200: z.object({
    orgUsers: z.array(organizationUserSchema),
  }),
};
export type GetOrgUsersFromEmailRes = z.infer<(typeof getOrgUsersFromEmailResSchema)[200]>;

///////////////////////////////////////////
// DeleteIntegration

export const deleteIntegrationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
    integrationId: mongoIdSchema,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type DeleteIntegrationReq = z.infer<typeof deleteIntegrationReqSchema>;

export const deleteIntegrationResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type DeleteIntegrationRes = z.infer<(typeof deleteIntegrationResSchema)[200]>;

///////////////////////////////////////////
// UpdateIntegration

export const updateIntegrationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
    integrationId: mongoIdSchema,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      externalName: z
        .string()
        .min(1, OrganizationIntegrationError.NameRequired)
        .max(255, OrganizationIntegrationError.InvalidName),
    })
    .strip(),
});
export type UpdateIntegrationReq = z.infer<typeof updateIntegrationReqSchema>;

export const updateIntegrationResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type UpdateIntegrationRes = z.infer<(typeof updateIntegrationResSchema)[200]>;

///////////////////////////////////////////
// Shopify

// AddShopifyIntegration
export const addShopifyIntegrationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      externalName: z
        .string()
        .min(1, OrganizationIntegrationError.NameRequired)
        .max(255, OrganizationIntegrationError.InvalidName),
      externalUrl: shopifyExternalUrlSchema,
      clientId: z.string().min(1, OrganizationIntegrationError.IntegrationMissingCredentials),
      clientSecret: z.string().min(1, OrganizationIntegrationError.IntegrationMissingCredentials),
    })
    .strip(),
});
export type AddShopifyIntegrationReq = z.infer<typeof addShopifyIntegrationReqSchema>;

export const addShopifyIntegrationResSchema = {
  200: z.object({
    data: z.object({
      oauthUrl: z.string().url(OrganizationIntegrationError.InvalidOauthUrl),
    }),
  }),
};
export type AddShopifyIntegrationRes = z.infer<(typeof addShopifyIntegrationResSchema)[200]>;

// ShopifyCallback
export const shopifyCallbackReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({
    state: z.string().min(1, 'State parameter is required'),
    code: z.string().optional(), // Optional - missing if user denied access
    hmac: z.string().optional(), // Optional - Shopify HMAC for verification
    shop: z.string().optional(), // Optional - store domain
    timestamp: z.string().optional(), // Request timestamp for HMAC verification
  }),
  body: z.object({}).nullish(),
});
export type ShopifyCallbackReq = z.infer<typeof shopifyCallbackReqSchema>;

// Google Search Console OAuth callback (Google redirects to a fixed path; org is encoded in `state`)
export const gscCallbackReqSchema = z.object({
  params: z.object({}),
  query: z.object({
    state: z.string().min(1, 'State parameter is required'),
    code: z.string().optional(),
    error: z.string().optional(),
  }),
  body: z.object({}).nullish(),
});
export type GscCallbackReq = z.infer<typeof gscCallbackReqSchema>;

// Note: Callback returns a redirect, not JSON, so no response schema needed

// GetShopifyBlogs
export const getShopifyBlogsReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
    integrationId: mongoIdSchema,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetShopifyBlogsReq = z.infer<typeof getShopifyBlogsReqSchema>;

export const getShopifyBlogsResSchema = {
  200: z.object({
    data: z.array(
      z
        .object({
          id: z.number(),
          handle: z.string(),
          title: z.string(),
        })
        .strip()
    ),
  }),
};
export type GetShopifyBlogsRes = z.infer<(typeof getShopifyBlogsResSchema)[200]>;

// GetIntegrations - List all integrations for an organization across all providers
export const getIntegrationsReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z
    .object({
      status: z.nativeEnum(IntegrationStatus).optional(),
    })
    .nullish(),
  body: z.object({}).nullish(),
});
export type GetIntegrationsReq = z.infer<typeof getIntegrationsReqSchema>;

// GetIntegrations Response - excludes sensitive fields (accessKey, refreshAccessKey, oauthState, oauthStateExpiresAt, accessExpiresAt)
export const getIntegrationsResSchema = {
  200: z.object({
    data: z.array(
      z
        .object({
          _id: mongoIdSchema,
          organizationId: mongoIdSchema,
          provider: z.nativeEnum(IntegrationProvider),
          status: z.nativeEnum(IntegrationStatus),

          externalName: z.string().optional(),
          externalUrl: z.string().optional(),
          scope: z.string().optional(), // Shopify
          accessUser: z.string().optional(), // WordPress

          createdAt: z.number(),
          updatedAt: z.number(),
        })
        .strip()
    ),
  }),
};
export type GetIntegrationsRes = z.infer<(typeof getIntegrationsResSchema)[200]>;

///////////////////////////////////////////
// Wordpress

// AddWordpressIntegration
export const addWordpressIntegrationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      externalName: z.string().min(1, OrganizationIntegrationError.NameRequired),
      externalUrl: externalUrlSchema,
      accessKey: z.string().min(1),
    })
    .strip(),
});
export type AddWordpressIntegrationReq = z.infer<typeof addWordpressIntegrationReqSchema>;

export const addWordpressIntegrationResSchema = {
  200: z.object({
    data: z
      .object({
        _id: mongoIdSchema,
        organizationId: mongoIdSchema,
        provider: z.nativeEnum(IntegrationProvider),
        status: z.nativeEnum(IntegrationStatus),

        externalName: z.string(),
        externalUrl: z.string(),
        createdAt: z.number(),
        updatedAt: z.number(),
      })
      .strip(),
  }),
};
export type AddWordpressIntegrationRes = z.infer<(typeof addWordpressIntegrationResSchema)[200]>;

///////////////////////////////////////////
// Bloggle

// AddBloggleIntegration
export const addBloggleIntegrationReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z
    .object({
      externalName: z.string().min(1, OrganizationIntegrationError.NameRequired),
      // bglt_ prefix + 32-byte hex string (64 hex characters)
      accessKey: z.string().regex(/^bglt_[0-9a-f]{64}$/, OrganizationIntegrationError.InvalidToken),
    })
    .strip(),
});
export type AddBloggleIntegrationReq = z.infer<typeof addBloggleIntegrationReqSchema>;

export const addBloggleIntegrationResSchema = {
  200: z.object({
    data: z
      .object({
        _id: mongoIdSchema,
        organizationId: mongoIdSchema,
        provider: z.nativeEnum(IntegrationProvider),
        status: z.nativeEnum(IntegrationStatus),
        externalName: z.string(),
        externalUrl: z.string(),
        createdAt: z.number(),
        updatedAt: z.number(),
      })
      .strip(),
  }),
};
export type AddBloggleIntegrationRes = z.infer<(typeof addBloggleIntegrationResSchema)[200]>;
// GetWordpressAuthors

export const getWordpressAuthorsReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
    integrationId: mongoIdSchema,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetWordpressAuthorsReq = z.infer<typeof getWordpressAuthorsReqSchema>;

export const getWordpressAuthorsResSchema = {
  200: z.object({
    data: z.array(
      z.object({
        login: z.string(),
        displayName: z.string(),
      })
    ),
  }),
};
export type GetWordpressAuthorsRes = z.infer<(typeof getWordpressAuthorsResSchema)[200]>;

///////////////////////////////////////////
// GetWordpressPluginDownloadUrl

export const getWordpressPluginDownloadUrlReqSchema = z.object({
  params: z.object({
    id: organizationSchema.shape._id,
  }),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetWordpressPluginDownloadUrlReq = z.infer<
  typeof getWordpressPluginDownloadUrlReqSchema
>;

export const getWordpressPluginDownloadUrlResSchema = {
  200: z.object({
    data: z.object({
      url: z.string().url(),
    }),
  }),
};
export type GetWordpressPluginDownloadUrlRes = z.infer<
  (typeof getWordpressPluginDownloadUrlResSchema)[200]
>;
